import React from 'react';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { useEditorStore } from './editor-store';
import { getActiveSectionId } from './helpers/get-active-section-id';
import { Rect } from './types';
import { shallow } from 'zustand/shallow';
import { createPropsEqualsChecker } from './utils/create-props-equals-checker';
import { useReactSpringScrollTo } from './utils/react-spring-scroll-to.hook';

type Props = {
  sectionCardRects: Rect[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
};

export const EditorCurrentSectionUpdater = ({ sectionCardRects, scrollContainerRef }: Props) => {
  const setCurrentSectionId = useEditorStore((state) => state.setCurrentSectionId);

  const currentSectionId = useEditorStore((state) => state.currentSectionId);
  const scrollToSectionId = useEditorStore((state) => state.scrollToSectionId);
  const canvasScrollPosition = useEditorStore((state) => state.canvasScrollPosition);
  const outerAreaRect = useEditorStore((state) => state.outerAreaRect);
  const layout = useEditorStore((state) => state.layout, shallow);

  const { sections, sectionLayoutInfos, innerActualHeight } = layout;

  const updateActiveSectionId = useRefCallback(() => {
    if (!outerAreaRect) return null;
    if (!innerActualHeight) return null;
    if (sections.length === 0) return null;
    const scrollBasedActiveId = getActiveSectionId({
      sections,
      sectionLayoutInfos,
      innerAreaHeight: outerAreaRect.height,
      innerActualHeight,
      canvasScrollPosition,
    });
    if (!scrollBasedActiveId) return;
    if (scrollToSectionId) return;
    if (scrollBasedActiveId === currentSectionId) return;
    // console.log('SectionBar: updateActiveSectionId:', 'active section', scrollBasedActiveId);
    setCurrentSectionId(scrollBasedActiveId);
  }, [
    outerAreaRect,
    innerActualHeight,
    sections,
    sectionLayoutInfos,
    canvasScrollPosition,
    currentSectionId,
    scrollToSectionId,
  ]);

  React.useEffect(() => {
    updateActiveSectionId();
  }, [canvasScrollPosition, innerActualHeight, outerAreaRect, sections.length, updateActiveSectionId]);

  const { scrollTo } = useReactSpringScrollTo();

  const scrollCurrentSectionCardIntoView = useRefCallback(() => {
    const currentSectionIndex = sections.findIndex((section) => section.id === currentSectionId);
    if (currentSectionIndex === -1) return;
    const currentSectionRect = sectionCardRects[currentSectionIndex];
    if (!currentSectionRect) return;
    const scrollContainer = scrollContainerRef.current as HTMLDivElement;
    // if the current section isn't completely within the scroll container, scroll it into view
    if (currentSectionRect.y < scrollContainer.scrollTop) {
      scrollTo(scrollContainer, currentSectionRect.y - 10);
    } else if (
      currentSectionRect.y + currentSectionRect.height >
      scrollContainer.scrollTop + scrollContainer.clientHeight
    ) {
      scrollTo(scrollContainer, currentSectionRect.y - scrollContainer.clientHeight + currentSectionRect.height + 10);
    }
  }, [currentSectionId, sections, sectionCardRects, scrollTo]);

  React.useEffect(() => {
    scrollCurrentSectionCardIntoView();
  }, [currentSectionId]);

  return <></>;
};

export const MemoizedEditorCurrentSectionUpdater = React.memo(
  EditorCurrentSectionUpdater,
  createPropsEqualsChecker([])
);
